import { Component, OnInit, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { CounselorNav } from '../counselor-nav/counselor-nav';
import { CareerPageService } from '../../../../services/career-page.service';
import { DatePipe } from '@angular/common';
import { CareerType } from '../../../../models/careerType';

// Angular Material
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatChipsModule } from '@angular/material/chips';
import { MatCardModule } from '@angular/material/card';
import { MatBadgeModule } from '@angular/material/badge';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDividerModule } from '@angular/material/divider';
import { Career } from '../../../../models/career';

/**
 * Displays the counselor's career listings with search and filter controls.
 * Supports filtering by status (Published/Draft) and category via a single chip set.
 */
@Component({
  selector: 'app-counselor-careers',
  standalone: true,
  imports: [
    CommonModule,
    RouterLink,
    FormsModule,
    CounselorNav,
    MatButtonModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatChipsModule,
    MatCardModule,
    MatBadgeModule,
    MatTooltipModule,
    MatDividerModule,
    DatePipe,
  ],
  templateUrl: './counselor-careers.html',
  styleUrl: './counselor-careers.css',
})
export class CounselorCareers implements OnInit {
  careerService = inject(CareerPageService);
  router = inject(Router);

  careers = signal<Career[]>([]);
  careerTypes = signal<CareerType[]>([]);
  searchQuery = '';
  /** Currently active filter chip value. */
  activeFilter = 'All';
  selectedCareer: Career | null = null;

  /**
   * Count of careers with 'Published' status.
   * @returns {number}
   */
  get publishedCount(): number {
    return this.careers().filter((c) => c.status === 'Published').length;
  }

  /**
   * List of filters including status and carrer types
   * @returns {string[]}
   */
  get filters() {
    return ['All', 'Published', 'Draft', ...this.careerTypes().map((ct) => ct.name)];
  }

  /**
   * Count of careers with 'Draft' status.
   * @returns {number}
   */
  get draftCount(): number {
    return this.careers().filter((c) => c.status === 'Draft').length;
  }

  /**
   * Get list of filtered careers
   * @returns {Career[]}
   */
  get filteredCareers() {
    let careers = this.careers();

    if (this.activeFilter && this.activeFilter !== 'All') {
      if (this.activeFilter === 'Published' || this.activeFilter === 'Draft') {
        careers = careers.filter((c) => c.status === this.activeFilter);
      } else {
        const type = this.careerTypes().find((ct) => ct.name === this.activeFilter);
        careers = careers.filter((c) => c.career_type_id === type?.id);
      }
    }

    if (this.searchQuery.trim()) {
      const q = this.searchQuery.toLowerCase();
      careers = careers.filter((c) => c.name.toLowerCase().includes(q));
    }

    return careers;
  }

  ngOnInit() {
    this.getCareers();
    this.getCareerTypes();
  }

  getCareers() {
    this.careerService.getCareers().subscribe({
      next: (careers) => {
        this.careers.set(careers);
      },
    });
  }

  getCareerTypes() {
    this.careerService.getTypes().subscribe({
      next: (types) => {
        this.careerTypes.set(types);
      },
    });
  }

  deleteCareer(career: Career) {
    this.careerService.deleteCareer(career!).subscribe({
      next: (careers) => {
        this.careers.set(careers);
      },
    });
  }

  updateCareer(career: Career) {
    this.router.navigate(['/counselors/careers/edit', career.id]);
  }

  openPage(career: Career) {
    this.router.navigate(['/counselors/careers', career.id]);
  }
}
